import {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { getUnitTranslation } from "../../translations/translations";

/** Описание одной жидкости, закачанной в скважину. */
export interface Fluid {
  /** Уникальный идентификатор — нужен для стабильного reconciliation при частых обновлениях. */
  id: string | number;
  title: string;
  /** Глубина начала жидкости (м), отсчёт сверху. */
  topDepth: number;
  /** Глубина окончания жидкости (м). */
  bottomDepth: number;
  /** Цвет секции. */
  color: string;
  /** true — жидкость в затрубе (обсадная колонна), false — в трубе (центр). */
  isAnnulus: boolean;
  /** Номер стадии закачки — показывается в подсказке. */
  stage?: number;
}

/** Обсадная колонна. Колонн может быть несколько (телескоп). */
export interface Casing {
  /** Уникальный идентификатор. */
  id: string | number;
  /** Диаметр колонны. Единица измерения задаётся через `grid.diameterUnit`. */
  diameter: number;
  /** Глубина башмака колонны (м). */
  depth: number;
}

/**
 * Палитра схемы. Любое поле можно опустить — берётся значение из `DEFAULT_COLORS`.
 * Компонент не знает о теме приложения: цвета передаёт вызывающий код.
 */
export interface WellCementingColors {
  /** Обводка секций жидкостей. */
  stroke?: string;
  /** Текст подписей шкалы, легенды и подсказки. */
  text?: string;
  /** Линии размерной сетки. */
  grid?: string;
  /** Обсадные колонны, башмаки и забой. */
  casing?: string;
  /** Стенки центральной трубы. */
  pipe?: string;
  /** Линия устья. */
  wellhead?: string;
  /** Заливка пустого ствола. */
  wellBackground?: string;
  /** Косая штриховка секции под курсором. */
  hatch?: string;
  /** Рамка цветных маркеров жидкостей (в легенде, подсказке и на шкале). */
  swatchBorder?: string;
  /** Фон всплывающей подсказки. */
  tooltipBackground?: string;
  /** Рамка всплывающей подсказки. */
  tooltipBorder?: string;
  /** Тень всплывающей подсказки (значение CSS box-shadow). */
  tooltipShadow?: string;
}

/** Полностью разрешённая палитра — все цвета известны. */
type Palette = Required<WellCementingColors>;

export interface WellCementingProps {
  /** Заголовок над схемой. Пусто — заголовок не рисуется. */
  title?: string;
  /** Полная глубина скважины (м). Сверху 0, снизу depth. */
  depth: number;
  /** Глубина трубы (м) — до этой отметки идёт центральная труба, ниже башмака открытый ствол. По умолчанию равна глубине скважины. */
  pipeDepth?: number;
  /** Обсадные колонны (телескоп). Если не заданы — рисуется одна колонна на всю глубину. */
  casings?: Casing[];
  /** Список закачанных жидкостей. */
  fluids: Fluid[];
  /** Ширина области отрисовки: число (px) или строка CSS (например, "100%"). */
  width?: number | string;
  /** Высота области отрисовки: число (px) или строка CSS. */
  height?: number | string;
  /** Цвет обводки секций жидкостей. Приоритетнее `colors.stroke`. */
  strokeColor?: string;
  /** Цвет шрифта подписей, легенды и подсказки. Приоритетнее `colors.text`. */
  fontColor?: string;
  /** Палитра остальных элементов схемы. Не заданные поля берутся из `DEFAULT_COLORS`. */
  colors?: WellCementingColors;
  /** Толщина обводки секций (в единицах viewBox). 0 — без обводки. */
  strokeWidth?: number;
  /** Настройка размерной сетки глубин. */
  grid?: GridConfig;
  /** Настройка размеров элементов схемы (геометрия в единицах viewBox). */
  layout?: LayoutConfig;
  /** Показывать легенду жидкостей под схемой. */
  showLegend?: boolean;
}

/** Настройка размеров элементов схемы. Любое поле можно опустить — берётся значение по умолчанию. */
export interface LayoutConfig {
  /**
   * Логическая высота системы координат (задаёт пропорции/аспект).
   * Если не задана — подбирается по реальной высоте компонента: схема занимает
   * всю высоту минус `viewInset`, а масштаб задаётся шириной (1 ед. viewBox = 1 px).
   */
  viewHeight?: number;
  /** Вертикальный отступ (px) сверху и снизу при автоподборе `viewHeight`. */
  viewInset?: number;
  /** Верхнее поле (под линию устья). */
  marginTop?: number;
  /** Нижнее поле. */
  marginBottom?: number;
  /** Ширина поля слева под шкалу глубин. */
  leftGutter?: number;
  /** Ширина поля справа под подписи. */
  rightGutter?: number;
  /** Ширина ствола (затрубья). */
  wellWidth?: number;
  /** Ширина трубы. */
  pipeWidth?: number;
  /** Размер треугольника башмака колонны. */
  shoeSize?: number;
}

/** Параметры размерной сетки глубин. Любое поле можно опустить — берётся значение по умолчанию. */
export interface GridConfig {
  /** Показывать сетку. */
  show?: boolean;
  /** Фиксированный шаг делений (м). Если не задан — подбирается автоматически по `targetTicks`. */
  step?: number;
  /** Желаемое число делений при автоподборе шага. */
  targetTicks?: number;
  /** Цвет линий сетки. По умолчанию — `colors.grid`. */
  color?: string;
  /** Толщина линий сетки (в единицах viewBox). */
  lineWidth?: number;
  /** Протягивать линии через весь ствол (а не только в поле подписей). */
  extend?: boolean;
  /** Показывать числовые подписи глубин. */
  showLabels?: boolean;
  /** Всегда подписывать конечную глубину (забой) у башмака, даже если она не попадает на шаг сетки. */
  showEndDepth?: boolean;
  /** Подписывать на шкале глубину верха каждой жидкости. */
  showFluidTops?: boolean;
  /** Минимальный вертикальный зазор между подписями (в единицах viewBox). При наслоении подпись уходит на другую сторону. */
  labelMinGap?: number;
  /** Цвет подписей. По умолчанию — `fontColor`. */
  labelColor?: string;
  /** Размер шрифта подписей (в единицах viewBox). */
  labelSize?: number;
  /** Режим переноса длинных подписей: по словам, по буквам или без переноса. */
  labelWrap?: WrapMode;
  /** Межстрочный интервал переносимых подписей (множитель кегля). */
  labelLineHeight?: number;
  /** Единица измерения, приписываемая к каждой отметке глубины. Пусто — только число. */
  unit?: string;
  /** Единица измерения, приписываемая к диаметру обсадных колонн. Пусто — только число. */
  diameterUnit?: string;
  /** Число знаков после запятой в подписях глубин и подсказке. */
  precision?: number;
  /** Число знаков после запятой в подписях диаметров. По умолчанию — `precision`. */
  diameterPrecision?: number;
  /**
   * Расположение диаметра относительно высоты в подписи обсадной колонны:
   * `"stack"` — всегда отдельной строкой над высотой; `"overflow"` (по умолчанию) —
   * в одну строку с высотой, а на отдельную строку сверху только при нехватке места.
   */
  casingLabelLayout?: CasingLabelLayout;
}

/** Режим переноса подписей. */
export type WrapMode = "word" | "char" | "none";

/** Расположение диаметра в подписи обсадной колонны: всегда сверху или только при оверфлоу. */
export type CasingLabelLayout = "stack" | "overflow";

/** Значения по умолчанию сетки, не зависящие от темы (цвета добавляет `gridDefaults`). */
const DEFAULT_GRID: Required<Omit<GridConfig, "color" | "labelColor">> = {
  show: true,
  step: 0, // 0 — автоподбор
  targetTicks: 6,
  lineWidth: 1,
  extend: false,
  showLabels: true,
  showEndDepth: true,
  showFluidTops: true,
  labelMinGap: 12,
  labelSize: 9,
  labelWrap: "word",
  labelLineHeight: 1.15,
  unit: "м",
  diameterUnit: "",
  precision: 2,
  diameterPrecision: 2,
  casingLabelLayout: "overflow",
};

/** Значения по умолчанию сетки с цветами из палитры. */
function gridDefaults(p: Palette): Required<GridConfig> {
  return { ...DEFAULT_GRID, color: p.grid, labelColor: p.text };
}

const DEFAULT_LAYOUT: Required<LayoutConfig> = {
  viewHeight: 620, // фолбэк, пока реальный размер не измерен (SSR/первый кадр)
  viewInset: 0,
  marginTop: 28, // место под линию устья
  marginBottom: 22,
  leftGutter: 64,
  rightGutter: 64,
  wellWidth: 172,
  pipeWidth: 56,
  shoeSize: 9,
};

const GUTTER_PAD = 18; // длина делений/коннекторов в полях слева и справа
const SWATCH_GAP = 6; // зазор между числом глубины и цветным маркером жидкости
// Половина высоты прописной буквы в долях кегля: сдвиг базовой линии для центровки строки по отметке.
const CAP_CENTER = 0.355;
const HOVER_BRIGHTNESS = 1.18; // подсветка секции жидкости под курсором (<1 — затемнение)
const HATCH_ID = "fluidHatch";
const HATCH_STEP = 8; // шаг косой штриховки при наведении (единицы viewBox)
const CURSOR_GAP = 14; // отступ подсказки от курсора
const EDGE_PAD = 4; // минимальный зазор подсказки до края области схемы

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

/**
 * Слияние настроек с значениями по умолчанию.
 * В отличие от spread, явный `undefined` в пропах не затирает значение по умолчанию —
 * вызывающий может передавать «ещё не загруженные» поля напрямую.
 */
function withDefaults<T extends object>(
  defaults: T,
  overrides?: Partial<T>
): T {
  if (!overrides) return defaults;
  const merged = { ...defaults };
  for (const key of Object.keys(overrides) as (keyof T)[]) {
    const value = overrides[key];
    if (value !== undefined) merged[key] = value as T[keyof T];
  }
  return merged;
}

/** Палитра по умолчанию (светлая). Экспортируется, чтобы её можно было взять за основу. */
export const DEFAULT_COLORS: Palette = {
  stroke: "#33373c",
  text: "#6b7480",
  grid: "#cdd4dc",
  casing: "#5a6573",
  pipe: "#39424e",
  wellhead: "#2b3340",
  wellBackground: "#f4f6f8",
  hatch: "#ffffff",
  swatchBorder: "rgba(0,0,0,.2)",
  tooltipBackground: "#ffffff",
  tooltipBorder: "#cdd4dc",
  tooltipShadow: "0 4px 12px rgba(0,0,0,.15)",
};

/** `DEFAULT_COLORS` → `colors` → отдельные пропсы `strokeColor`/`fontColor` (каждый следующий приоритетнее). */
function resolvePalette(
  overrides: WellCementingColors | undefined,
  strokeColor: string | undefined,
  fontColor: string | undefined
): Palette {
  const p = withDefaults({ ...DEFAULT_COLORS }, overrides);
  if (strokeColor !== undefined) p.stroke = strokeColor;
  if (fontColor !== undefined) p.text = fontColor;
  return p;
}

/** Вычисленная геометрия схемы в координатах viewBox. */
interface Geom {
  vbW: number;
  vbH: number;
  wellTop: number;
  wellBottom: number;
  axisX: number; // правый край левой шкалы подписей
  rightAxisX: number; // левый край правой колонки подписей
  wellLeft: number;
  wellRight: number;
  centerX: number;
  pipeLeft: number;
  pipeRight: number;
  pipeWidth: number;
  shoe: number;
}

/** Строит геометрию из настроек размеров. */
function computeGeom(L: Required<LayoutConfig>): Geom {
  const wellLeft = L.leftGutter;
  const wellRight = wellLeft + L.wellWidth;
  const centerX = (wellLeft + wellRight) / 2;
  return {
    vbW: wellRight + L.rightGutter,
    vbH: L.viewHeight,
    wellTop: L.marginTop,
    wellBottom: L.viewHeight - L.marginBottom,
    axisX: wellLeft - GUTTER_PAD,
    rightAxisX: wellRight + GUTTER_PAD,
    wellLeft,
    wellRight,
    centerX,
    pipeLeft: centerX - L.pipeWidth / 2,
    pipeRight: centerX + L.pipeWidth / 2,
    pipeWidth: L.pipeWidth,
    shoe: L.shoeSize,
  };
}

/** Геометрия колонны для отрисовки. */
interface CasingGeom {
  id: string | number;
  diameter: number;
  depth: number; // глубина башмака (м), ограниченная стволом
  y: number; // Y башмака
}

/**
 * Колонны рисуются не телескопически: все на краю затрубья (у стенки ствола),
 * различаются только глубиной башмака. Диаметр остаётся лишь в подписи.
 */
function layoutCasings(
  casings: Casing[],
  yOf: (d: number) => number,
  depth: number
): CasingGeom[] {
  return casings.map((c) => {
    const d = Math.min(Math.max(c.depth, 0), depth);
    return { id: c.id, diameter: c.diameter, depth: d, y: yOf(d) };
  });
}

/**
 * Прямоугольники одной секции жидкости.
 * Для затруба — две полосы (слева и справа от трубы), для трубы — одна центральная.
 * Вынесено в memo-компонент: при обновлении данных перерисовываются только изменившиеся секции.
 */
const FluidSection = memo(function FluidSection({
  id,
  color,
  yTop,
  yBottom,
  isAnnulus,
  pipeEndY,
  geom,
  stroke,
  strokeWidth,
  hovered,
  onHover,
  onLeave,
}: {
  /** Идентификатор жидкости — по нему родитель находит данные для подсказки. */
  id: string | number;
  color: string;
  yTop: number;
  yBottom: number;
  isAnnulus: boolean;
  /** Y-координата башмака трубы: выше — есть труба, ниже — открытый ствол. */
  pipeEndY: number;
  /** Геометрия схемы — стабильная ссылка, чтобы memo не сбрасывался на частых обновлениях. */
  geom: Geom;
  stroke: string;
  strokeWidth: number;
  /** Секция под курсором — затемняется. */
  hovered: boolean;
  /** Стабильные колбэки — иначе memo сбрасывался бы на каждом рендере родителя. */
  onHover: (e: ReactPointerEvent, id: string | number) => void;
  onLeave: () => void;
}) {
  // Колбэки создаются внутри memo-компонента, поэтому пропсы остаются стабильными.
  const hoverProps = {
    onPointerEnter: (e: ReactPointerEvent) => onHover(e, id),
    onPointerMove: (e: ReactPointerEvent) => onHover(e, id),
    onPointerLeave: onLeave,
    style: {
      cursor: "pointer",
      filter: hovered ? `brightness(${HOVER_BRIGHTNESS})` : undefined,
      transition: "filter .12s ease-out",
    },
  };

  if (yBottom - yTop <= 0) return null;

  const { wellLeft, wellRight, pipeLeft, pipeRight, pipeWidth } = geom;

  // Обводка центрируется на ребре — сдвигаемся внутрь, чтобы линия не выходила за стенки.
  const inset = strokeWidth / 2;
  const strokeProps = strokeWidth > 0 ? { stroke, strokeWidth } : undefined;

  // Прямоугольники секции считаются один раз: ими же рисуется штриховка при наведении.
  const rects: { x: number; y: number; width: number; height: number }[] = [];

  if (isAnnulus) {
    const leftW = pipeLeft - wellLeft;
    const rightW = wellRight - pipeRight;
    const fullW = wellRight - wellLeft;
    // Выше башмака — две боковые полосы (вокруг трубы); ниже башмака — на всю ширину ствола.
    const splitY = Math.min(Math.max(pipeEndY, yTop), yBottom);
    const bandH = splitY - yTop;
    const fullH = yBottom - splitY;
    if (bandH > 0) {
      rects.push({
        x: wellLeft + inset,
        y: yTop,
        width: leftW - inset,
        height: bandH,
      });
      rects.push({
        x: pipeRight,
        y: yTop,
        width: rightW - inset,
        height: bandH,
      });
    }
    if (fullH > 0) {
      rects.push({
        x: wellLeft + inset,
        y: splitY,
        width: fullW - 2 * inset,
        height: fullH,
      });
    }
  } else {
    // Труба существует только до башмака — секцию в трубе обрезаем по нему.
    const hPipe = Math.min(yBottom, pipeEndY) - yTop;
    if (hPipe <= 0) return null;
    rects.push({ x: pipeLeft, y: yTop, width: pipeWidth, height: hPipe });
  }

  if (rects.length === 0) return null;

  const shapes = rects.map((r, i) => <rect key={i} {...r} />);

  return (
    <g {...hoverProps}>
      <g fill={color} {...strokeProps}>
        {shapes}
      </g>
      {/* Косая штриховка поверх заливки — только под курсором. */}
      {hovered && (
        <g fill={`url(#${HATCH_ID})`} style={{ pointerEvents: "none" }}>
          {shapes}
        </g>
      )}
    </g>
  );
});

interface GridLabel {
  /** Тип подписи: верх жидкости или башмак обсадной колонны. */
  kind: "fluid" | "casing";
  depth: number;
  y: number;
  color: string;
  /** Идентификатор жидкости (для kind === "fluid") — по нему наведение на подпись подсвечивает секцию. */
  fluidId?: string | number;
  /** Название жидкости (для kind === "fluid"). */
  title?: string;
  /** Диаметр колонны (для kind === "casing"). */
  diameter?: number;
  side: "left" | "right";
}

/**
 * Раскладывает подписи по двум сторонам графика: по умолчанию слева,
 * а при наслоении на предыдущую (зазор меньше minGap) — справа.
 * `reservedLeftY` — уже занятые слева отметки (деления шкалы, забой): рядом с ними
 * подпись тоже уходит вправо.
 */
function assignSides<T extends { y: number }>(
  items: T[],
  minGap: number,
  reservedLeftY: number[] = []
): (T & { side: "left" | "right" })[] {
  const sorted = [...items].sort((a, b) => a.y - b.y);
  const leftBusy = (y: number) =>
    reservedLeftY.some((r) => Math.abs(r - y) < minGap);
  let lastLeft = -Infinity;
  let lastRight = -Infinity;
  return sorted.map((it) => {
    const leftFree = !leftBusy(it.y);
    let side: "left" | "right";
    if (leftFree && it.y - lastLeft >= minGap) side = "left";
    else if (it.y - lastRight >= minGap) side = "right";
    // обе заняты — берём сторону с большим зазором, но слева только если там нет отметки шкалы
    else
      side = leftFree && it.y - lastLeft >= it.y - lastRight ? "left" : "right";
    if (side === "left") lastLeft = it.y;
    else lastRight = it.y;
    return { ...it, side };
  });
}

/** Собирает подписи сетки: верхи жидкостей и башмаки обсадных колонн, разложенные по сторонам. */
function buildGridLabels(
  fluids: Fluid[],
  casings: CasingGeom[],
  showFluidTops: boolean,
  yOf: (d: number) => number,
  ticks: { d: number; y: number }[],
  depth: number,
  minGap: number,
  precision: number,
  /** Цвет коннекторов подписей обсадных колонн. */
  casingColor: string,
  /** Отметки, уже подписанные слева (забой и т.п.) — помимо делений сетки. */
  reservedLeftY: number[] = []
): GridLabel[] {
  const items: Omit<GridLabel, "side">[] = [];

  if (showFluidTops) {
    // Уникальные верхы по округлённой (до точности отображения) глубине — труба и затруб на одной глубине дают одну подпись.
    const byDepth = new Map<
      number,
      { color: string; title: string; fluidId: string | number }
    >();
    for (const f of fluids) {
      const key = roundDepth(Math.min(f.topDepth, f.bottomDepth), precision);
      if (key <= 0 || key >= depth) continue; // устье и забой подписаны отдельно
      if (ticks.some((t) => Math.abs(t.d - key) < depthEps(precision)))
        continue; // уже есть на сетке
      if (!byDepth.has(key))
        byDepth.set(key, { color: f.color, title: f.title, fluidId: f.id });
    }
    byDepth.forEach(({ color, title, fluidId }, d) => {
      items.push({ kind: "fluid", depth: d, y: yOf(d), color, title, fluidId });
    });
  }

  // Башмак каждой колонны — подписываем всегда (это ключевая отметка конструкции).
  for (const c of casings) {
    items.push({
      kind: "casing",
      depth: roundDepth(c.depth, precision),
      y: c.y,
      color: casingColor,
      diameter: c.diameter,
    });
  }

  // Подписи делений шкалы и забоя занимают левое поле — рядом с ними уходим вправо.
  return assignSides(items, minGap, [
    ...ticks.map((t) => t.y),
    ...reservedLeftY,
  ]);
}

const FONT_FAMILY = "system-ui, sans-serif";

// Измеритель ширины текста через canvas — без вставки в DOM и reflow.
// Кэш по `${fontSize}|${text}` (заголовков немного и они стабильны).
const measureCtx: CanvasRenderingContext2D | null =
  typeof document !== "undefined"
    ? document.createElement("canvas").getContext("2d")
    : null;
const measureCache = new Map<string, number>();

function measureText(text: string, fontSize: number, bold = false): number {
  if (!measureCtx) return text.length * fontSize * (bold ? 0.62 : 0.58); // SSR/фолбэк
  const key = `${bold ? "b" : "n"}|${fontSize}|${text}`;
  let w = measureCache.get(key);
  if (w === undefined) {
    measureCtx.font = `${bold ? "bold " : ""}${fontSize}px ${FONT_FAMILY}`;
    w = measureCtx.measureText(text).width;
    measureCache.set(key, w);
  }
  return w;
}

/** Форматирует число для отображения с заданной точностью. */
function formatNumber(d: number, precision: number): string {
  return d.toFixed(precision);
}

/** Округляет глубину до точности отображения (для ключей сопоставления/дедупликации). */
function roundDepth(d: number, precision: number): number {
  const factor = Math.pow(10, precision);
  return Math.round(d * factor) / factor;
}

/** Порог совпадения двух глубин при заданной точности — половина последнего разряда. */
function depthEps(precision: number): number {
  return 0.5 * Math.pow(10, -precision);
}

/** Реальный размер элемента (px), отслеживаемый через ResizeObserver. */
function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      // Обновляем только при заметном изменении — иначе дробные пиксели дёргают перерисовку.
      setSize((prev) =>
        prev &&
        Math.abs(prev.w - width) < 0.5 &&
        Math.abs(prev.h - height) < 0.5
          ? prev
          : { w: width, h: height }
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, size] as const;
}

/** Положение подсказки относительно области схемы (px). */
interface HoverState {
  id: string | number;
  x: number;
  y: number;
}

/** Всплывающая подсказка по столбу жидкости. */
function FluidTooltip({
  fluid,
  at,
  box,
  unit,
  precision,
  palette,
}: {
  fluid: Fluid;
  at: HoverState;
  /** Размер области схемы — нужен, чтобы подсказка не уезжала за край. */
  box: { w: number; h: number } | null;
  unit: string;
  /** Число знаков после запятой в глубинах. */
  precision: number;
  /** Цвета темы приложения. */
  palette: Palette;
}) {
  const suffix = unit ? ` ${unit}` : "";

  // Реальный размер подсказки — по нему её прижимаем к границам области схемы.
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    // Без dep-массива: при неизменном размере setState — no-op, цикла не будет.
    setSize((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
  });

  let left = at.x + CURSOR_GAP;
  let top = at.y + CURSOR_GAP;
  if (size && box) {
    // Не помещается справа/снизу от курсора — переносим на другую сторону, затем прижимаем к краю.
    if (left + size.w > box.w - EDGE_PAD) left = at.x - CURSOR_GAP - size.w;
    if (top + size.h > box.h - EDGE_PAD) top = at.y - CURSOR_GAP - size.h;
    left = clamp(left, EDGE_PAD, Math.max(EDGE_PAD, box.w - size.w - EDGE_PAD));
    top = clamp(top, EDGE_PAD, Math.max(EDGE_PAD, box.h - size.h - EDGE_PAD));
  }

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        left,
        top,
        // До первого измерения позиция ещё не уточнена — не показываем, чтобы не мигало.
        visibility: size ? "visible" : "hidden",
        maxWidth: box ? Math.max(140, box.w - 2 * EDGE_PAD) : 260,
        pointerEvents: "none",
        zIndex: 2,
        background: palette.tooltipBackground,
        color: palette.text,
        border: `1px solid ${palette.tooltipBorder}`,
        borderRadius: 6,
        boxShadow: palette.tooltipShadow,
        padding: "8px 10px",
        fontFamily: FONT_FAMILY,
        fontSize: 12,
        lineHeight: 1.45,
        // Длинное название переносится, а не растягивает подсказку за границы.
        overflowWrap: "anywhere",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          fontWeight: 600,
        }}
      >
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 3,
            background: fluid.color,
            border: `1px solid ${palette.swatchBorder}`,
            flex: "0 0 auto",
            marginTop: 3,
          }}
        />
        <span style={{ minWidth: 0 }}>{fluid.title}</span>
      </div>
      {fluid.stage !== undefined && <div>Стадия: {fluid.stage}</div>}
      <div>
        Верх:{" "}
        {formatNumber(Math.min(fluid.topDepth, fluid.bottomDepth), precision)}
        {suffix}
      </div>
      <div>
        Низ:{" "}
        {formatNumber(Math.max(fluid.topDepth, fluid.bottomDepth), precision)}
        {suffix}
      </div>
      <div>{fluid.isAnnulus ? "В кольце" : "В колонне"}</div>
    </div>
  );
}

/**
 * Легенда под схемой. Дубли по названию схлопываются.
 * Раскладывается в строку с переносом — под схемой ширина ограничена сильнее, чем высота.
 */
const Legend = memo(function Legend({
  fluids,
  fontSize,
  color,
  swatchBorder,
}: {
  fluids: Fluid[];
  fontSize: number;
  color: string;
  /** Рамка цветного маркера жидкости. */
  swatchBorder: string;
}) {
  const rows = useMemo(() => {
    const seen = new Set<string>();
    return fluids.filter((f) => {
      var key = `${f.title}-${f.stage}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [fluids]);

  if (rows.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px 16px",
        padding: "8px 4px 8px 16px",
        fontFamily: FONT_FAMILY,
        fontSize,
        color,
        flex: "0 0 auto",
      }}
    >
      {rows.map((f) => (
        <div
          key={f.title}
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: 3,
              background: f.color,
              border: `1px solid ${swatchBorder}`,
              flex: "0 0 auto",
            }}
          />
          <span style={{ fontWeight: 600 }}>{f.title}</span>
        </div>
      ))}
    </div>
  );
});

/**
 * Объединяет соседние секции одной жидкости в одну.
 * Ключ — название (title) + стадия (stage) + путь (isAnnulus): бэкенд может присылать
 * одну жидкость несколькими смежными строками, их нужно показывать единым интервалом.
 * Диапазон объединения — от минимального верха до максимального низа группы.
 */
function mergeFluids(fluids: Fluid[]): Fluid[] {
  if (fluids.length < 2) return fluids;

  const byKey = new Map<string, Fluid>();
  const order: string[] = [];
  for (const f of fluids) {
    const key = `${f.title} ${f.stage} ${f.isAnnulus ? 1 : 0}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.topDepth = Math.min(existing.topDepth, f.topDepth);
      existing.bottomDepth = Math.max(existing.bottomDepth, f.bottomDepth);
    } else {
      byKey.set(key, { ...f });
      order.push(key);
    }
  }

  if (order.length === fluids.length) return fluids;
  return order.map((key) => byKey.get(key)!);
}

/** Подбор «круглого» шага для шкалы глубин. */
function niceStep(depth: number, targetTicks = 6): number {
  const raw = depth / targetTicks;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = norm >= 5 ? 5 : norm >= 2 ? 2 : 1;
  return step * mag;
}

function WellCementingBase({
  title,
  depth,
  pipeDepth,
  casings = [],
  fluids: rawFluids,
  width = 300,
  height = 620,
  strokeColor,
  fontColor,
  colors,
  strokeWidth = 0.75,
  grid,
  layout,
  showLegend = true,
}: WellCementingProps) {
  // Компонент автономен: цвета приходят пропсами, тему приложения подмешивает вызывающий код.
  const palette = useMemo(
    () => resolvePalette(colors, strokeColor, fontColor),
    [colors, strokeColor, fontColor]
  );

  // Смежные строки одной жидкости (одинаковые title + stage + isAnnulus) схлопываем в один интервал.
  const fluids = useMemo(() => mergeFluids(rawFluids), [rawFluids]);

  const g = withDefaults(gridDefaults(palette), grid);
  const base = withDefaults(DEFAULT_LAYOUT, layout);

  // Единицы измерения переводим на русский; если перевода нет — остаётся оригинал.
  const unitLabel = getUnitTranslation(g.unit);
  const diameterUnitLabel = getUnitTranslation(g.diameterUnit);

  const [hostRef, hostSize] = useElementSize<HTMLDivElement>();

  // Подсказка хранит только id — так колбэки остаются стабильными и memo секций не сбрасывается.
  const [hover, setHover] = useState<HoverState | null>(null);
  const handleFluidHover = useCallback(
    (e: ReactPointerEvent, id: string | number) => {
      const rect = hostRef.current?.getBoundingClientRect();
      if (!rect) return;
      setHover({ id, x: e.clientX - rect.left, y: e.clientY - rect.top });
    },
    [hostRef]
  );
  const handleFluidLeave = useCallback(() => setHover(null), []);
  const hoveredFluid = hover
    ? fluids.find((f) => f.id === hover.id)
    : undefined;
  // Глубина верха жидкости под курсором — по ней подсвечиваем её отметку на шкале.
  const hoveredTopKey = hoveredFluid
    ? roundDepth(
        Math.min(hoveredFluid.topDepth, hoveredFluid.bottomDepth),
        g.precision
      )
    : null;

  // Ширина viewBox задана настройками, поэтому масштаб схемы определяет ширина контейнера.
  // Подгоняем под неё логическую высоту: тогда 1 ед. viewBox = 1 px и схема занимает
  // всю высоту компонента за вычетом отступов сверху и снизу.
  const vbW = base.leftGutter + base.wellWidth + base.rightGutter;
  const autoViewHeight =
    hostSize && hostSize.w > 0 && hostSize.h > 0
      ? Math.max(
          1,
          Math.round((vbW * (hostSize.h - 2 * base.viewInset)) / hostSize.w)
        )
      : null;

  // Явно заданный viewHeight имеет приоритет над автоподбором.
  const L = {
    ...base,
    viewHeight: layout?.viewHeight ?? autoViewHeight ?? base.viewHeight,
  };

  // Геометрия размеров — стабильная ссылка между обновлениями данных (зависит только от настроек размеров).
  const geom = useMemo(
    () => computeGeom(L),
    [
      L.viewHeight,
      L.marginTop,
      L.marginBottom,
      L.leftGutter,
      L.rightGutter,
      L.wellWidth,
      L.pipeWidth,
      L.shoeSize,
    ]
  );

  // Глубина трубы по умолчанию — на всю скважину; ограничиваем диапазоном [0, depth].
  const pipeLen = Math.min(Math.max(pipeDepth ?? depth, 0), depth);

  // Шкала зависит только от геометрии, глубины и параметров шага — пересчитываем редко.
  const { yOf, ticks } = useMemo(() => {
    const top = geom.wellTop;
    const bottom = geom.wellBottom;
    const usable = bottom - top;
    const safeDepth = depth > 0 ? depth : 1;
    const yOf = (d: number) => top + (d / safeDepth) * usable;

    const step = g.step > 0 ? g.step : niceStep(safeDepth, g.targetTicks);
    const ticks: { d: number; y: number }[] = [];
    for (let d = 0; d <= safeDepth + 1e-6; d += step) {
      ticks.push({ d: roundDepth(d, g.precision), y: yOf(d) });
    }
    return { yOf, ticks };
  }, [geom, depth, g.step, g.targetTicks, g.precision]);

  const { wellTop, wellBottom } = geom;
  const pipeEndY = yOf(pipeLen); // башмак трубы

  // Геометрия обсадных колонн. Конструкция статична — считать на каждый рендер дёшево.
  const casingGeom = layoutCasings(casings, yOf, depth);
  // Самый глубокий башмак: ниже него обсадной колонны нет — открытый ствол.
  const deepestCasingY = casingGeom.length
    ? Math.max(...casingGeom.map((c) => c.y))
    : wellBottom;

  // Единица измерения приписывается к каждой отметке глубины.
  const depthLabel = (d: number) =>
    unitLabel
      ? `${formatNumber(d, g.precision)} ${unitLabel}`
      : formatNumber(d, g.precision);

  // Базовая линия текста, при которой строка визуально центрирована по отметке.
  const baselineAt = (y: number) => y + g.labelSize * CAP_CENTER;

  // Забой подписывается слева отдельно, если не совпал с делением сетки.
  const endDepthLabelled =
    g.showEndDepth &&
    depth > 0 &&
    !ticks.some((t) => Math.abs(t.d - depth) < depthEps(g.precision));

  // Подписи сетки: верхи жидкостей + башмаки колонн, разложенные по двум сторонам.
  const gridLabels = g.show
    ? buildGridLabels(
        fluids,
        casingGeom,
        g.showFluidTops,
        yOf,
        ticks,
        depth,
        g.labelMinGap,
        g.precision,
        palette.casing,
        endDepthLabelled ? [wellBottom] : []
      )
    : [];

  return (
    <div
      style={{
        width,
        height,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
      }}
    >
      {/* Заголовок вне viewBox — как и легенда, занимает высоту сверх схемы. */}
      {title && (
        <div
          style={{
            flex: "0 0 auto",
            // Снизу отступа нет: между заголовком и устьем уже есть поле viewBox (marginTop).
            padding: "8px 4px 0",
            textAlign: "center",
            fontFamily: FONT_FAMILY,
            fontSize: "1.1rem",
            fontWeight: 600,
            lineHeight: 1.2,
            color: palette.text,
          }}
        >
          {title}
        </div>
      )}

      {/* Измеряется только область схемы — легенда занимает высоту вне viewBox. */}
      <div
        ref={hostRef}
        style={{
          position: "relative",
          display: "flex",
          flex: "1 1 auto",
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${geom.vbW} ${geom.vbH}`}
          role="img"
          aria-label="Схема цементирования скважины"
          style={{
            fontFamily: FONT_FAMILY,
            userSelect: "none",
            display: "block",
          }}
        >
          <defs>
            {/* Косая штриховка для секции под курсором. */}
            <pattern
              id={HATCH_ID}
              width={HATCH_STEP}
              height={HATCH_STEP}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <line
                x1={0}
                y1={0}
                x2={0}
                y2={HATCH_STEP}
                stroke={palette.hatch}
                strokeWidth={HATCH_STEP / 2}
                opacity={0.4}
              />
            </pattern>

            {/* Жидкости рисуем только внутри ствола скважины. */}
            <clipPath id="wellClip">
              <rect
                x={geom.wellLeft}
                y={wellTop}
                width={geom.wellRight - geom.wellLeft}
                height={wellBottom - wellTop}
              />
            </clipPath>
          </defs>

          {/* Фон ствола (пустая скважина). */}
          <rect
            x={geom.wellLeft}
            y={wellTop}
            width={geom.wellRight - geom.wellLeft}
            height={wellBottom - wellTop}
            fill={palette.wellBackground}
          />

          {/* Секции жидкостей. */}
          <g clipPath="url(#wellClip)">
            {fluids.map((f) => (
              <FluidSection
                key={f.id}
                id={f.id}
                color={f.color}
                isAnnulus={f.isAnnulus}
                yTop={yOf(Math.min(f.topDepth, f.bottomDepth))}
                yBottom={yOf(Math.max(f.topDepth, f.bottomDepth))}
                pipeEndY={pipeEndY}
                geom={geom}
                stroke={palette.stroke}
                strokeWidth={strokeWidth}
                hovered={hover?.id === f.id}
                onHover={handleFluidHover}
                onLeave={handleFluidLeave}
              />
            ))}
          </g>

          {/* Забой скважины. Пунктир, если низ — открытый ствол (ниже самого глубокого башмака). */}
          <line
            x1={geom.wellLeft}
            y1={wellBottom}
            x2={geom.wellRight}
            y2={wellBottom}
            stroke={palette.casing}
            strokeWidth={2}
            strokeDasharray={deepestCasingY < wellBottom ? "4 3" : undefined}
          />

          {/* Обсадные колонны — все на краю затрубья, различаются глубиной башмака. */}
          {casingGeom.length === 0 ? (
            // По умолчанию — одна колонна на всю глубину.
            <g stroke={palette.casing} strokeWidth={2}>
              <line
                x1={geom.wellLeft}
                y1={wellTop}
                x2={geom.wellLeft}
                y2={wellBottom}
              />
              <line
                x1={geom.wellRight}
                y1={wellTop}
                x2={geom.wellRight}
                y2={wellBottom}
              />
            </g>
          ) : (
            casingGeom.map((c) => (
              <g key={c.id} stroke={palette.casing} strokeWidth={2}>
                <line
                  x1={geom.wellLeft}
                  y1={wellTop}
                  x2={geom.wellLeft}
                  y2={c.y}
                />
                <line
                  x1={geom.wellRight}
                  y1={wellTop}
                  x2={geom.wellRight}
                  y2={c.y}
                />
                {/* Башмак колонны — треугольники у стенок. */}
                <polygon
                  points={`${geom.wellLeft},${c.y - geom.shoe} ${
                    geom.wellLeft
                  },${c.y} ${geom.wellLeft - geom.shoe},${c.y}`}
                  fill={palette.casing}
                  stroke="none"
                />
                <polygon
                  points={`${geom.wellRight},${c.y - geom.shoe} ${
                    geom.wellRight
                  },${c.y} ${geom.wellRight + geom.shoe},${c.y}`}
                  fill={palette.casing}
                  stroke="none"
                />
              </g>
            ))
          )}

          {/* Открытый ствол ниже самого глубокого башмака — штриховая обводка. */}
          {casingGeom.length > 0 && deepestCasingY < wellBottom && (
            <g stroke={palette.casing} strokeWidth={2} strokeDasharray="4 3">
              <line
                x1={geom.wellLeft}
                y1={deepestCasingY}
                x2={geom.wellLeft}
                y2={wellBottom}
              />
              <line
                x1={geom.wellRight}
                y1={deepestCasingY}
                x2={geom.wellRight}
                y2={wellBottom}
              />
            </g>
          )}

          {/* Стенки трубы — до башмака (открыта снизу). */}
          <line
            x1={geom.pipeLeft}
            y1={wellTop}
            x2={geom.pipeLeft}
            y2={pipeEndY}
            stroke={palette.pipe}
            strokeWidth={2}
          />
          <line
            x1={geom.pipeRight}
            y1={wellTop}
            x2={geom.pipeRight}
            y2={pipeEndY}
            stroke={palette.pipe}
            strokeWidth={2}
          />
          {/* Башмак трубы — отметка наружу, если труба короче скважины. */}
          {pipeLen < depth && (
            <>
              <line
                x1={geom.pipeLeft - 4}
                y1={pipeEndY}
                x2={geom.pipeLeft}
                y2={pipeEndY}
                stroke={palette.pipe}
                strokeWidth={2}
              />
              <line
                x1={geom.pipeRight}
                y1={pipeEndY}
                x2={geom.pipeRight + 4}
                y2={pipeEndY}
                stroke={palette.pipe}
                strokeWidth={2}
              />
            </>
          )}

          {/* Линия устья. */}
          <line
            x1={geom.wellLeft - 8}
            y1={wellTop}
            x2={geom.wellRight + 4}
            y2={wellTop}
            stroke={palette.wellhead}
            strokeWidth={2}
          />

          {/* Размерная сетка глубин. */}
          {g.show && (
            <g fill={g.labelColor} fontSize={g.labelSize}>
              {ticks.map((t) => (
                <g key={t.d}>
                  <line
                    x1={geom.axisX}
                    y1={t.y}
                    x2={g.extend ? geom.wellRight : geom.wellLeft}
                    y2={t.y}
                    stroke={g.color}
                    strokeWidth={g.lineWidth}
                  />
                  {g.showLabels && (
                    <text
                      x={geom.axisX - 4}
                      y={baselineAt(t.y)}
                      textAnchor="end"
                    >
                      {depthLabel(t.d)}
                    </text>
                  )}
                </g>
              ))}

              {/* Конечная глубина (забой) — подписываем всегда, если её нет среди делений сетки. */}
              {endDepthLabelled && (
                <g fontWeight="bold">
                  <line
                    x1={geom.axisX}
                    y1={wellBottom}
                    x2={g.extend ? geom.wellRight : geom.wellLeft}
                    y2={wellBottom}
                    stroke={g.color}
                    strokeWidth={g.lineWidth}
                  />
                  {g.showLabels && (
                    <text
                      x={geom.axisX - 4}
                      y={baselineAt(wellBottom)}
                      textAnchor="end"
                    >
                      {depthLabel(depth)}
                    </text>
                  )}
                </g>
              )}

              {/* Подписи верхов жидкостей и башмаков колонн — слева, а при наслоении справа.
                  Длинный текст переносится по словам в пределах поля подписей.
                  Наведённую отметку рисуем последней — её подложка перекрывает соседние глубины. */}
              {(hoveredTopKey === null
                ? gridLabels
                : [...gridLabels].sort(
                    (a, b) =>
                      Number(
                        a.kind === "fluid" &&
                          Math.abs(a.depth - hoveredTopKey) <
                            depthEps(g.precision)
                      ) -
                      Number(
                        b.kind === "fluid" &&
                          Math.abs(b.depth - hoveredTopKey) <
                            depthEps(g.precision)
                      )
                  )
              ).map((l) => {
                // Жидкость обозначается цветным маркером (название — в легенде), колонна — диаметром.
                const isFluid = l.kind === "fluid";
                // Отметка жидкости под курсором — подсвечиваем её заголовок с глубиной.
                const isHovered =
                  isFluid &&
                  hoveredTopKey !== null &&
                  Math.abs(l.depth - hoveredTopKey) < depthEps(g.precision);
                const diameterSuffix = diameterUnitLabel
                  ? ` ${diameterUnitLabel}`
                  : "";
                const outer = isFluid
                  ? ""
                  : `⌀${formatNumber(
                      l.diameter ?? 0,
                      g.diameterPrecision
                    )}${diameterSuffix}`;
                const onLeft = l.side === "left";
                const anchorX = onLeft ? geom.axisX - 4 : geom.rightAxisX + 4;
                const anchor = onLeft ? "end" : "start";
                const depthStr = depthLabel(l.depth);

                // Маркер жидкости — сразу за числом глубины, снаружи от ствола.
                // Под курсором заголовок с глубиной укрупняется — вместе с ним растут маркер и мерка.
                const depthFontSize = isHovered
                  ? g.labelSize * 1.15
                  : g.labelSize;
                const swatch = depthFontSize * (isHovered ? 1.2 : 0.95);
                // Число рисуется жирным, поэтому и меряем его жирным — иначе маркер наезжает на текст.
                const depthW = measureText(depthStr, depthFontSize, true);
                const swatchX = onLeft
                  ? anchorX - depthW - SWATCH_GAP - swatch
                  : anchorX + depthW + SWATCH_GAP;

                // Доступная ширина поля подписей (px) и точная проверка через canvas.
                // Глубина рисуется жирным, поэтому и меряем её жирной — иначе оценка занижается
                // и слишком широкая подпись остаётся в одну строку, вылезая за поле.
                const fieldW = onLeft
                  ? geom.axisX - 6
                  : geom.vbW - (geom.rightAxisX + 6);
                const oneLineW =
                  measureText(`${outer} `, g.labelSize) +
                  measureText(depthStr, g.labelSize, true);
                // "stack" — диаметр всегда отдельной строкой; иначе одна строка, если помещается.
                const fits =
                  g.casingLabelLayout === "stack"
                    ? false
                    : g.labelWrap === "none" || oneLineW <= fieldW;

                // При оверфлоу диаметр уходит на отдельную строку над высотой (глубиной) колонны.
                const lines = [outer, depthStr];
                const lh = g.labelSize * g.labelLineHeight;
                const startDy =
                  -((lines.length - 1) / 2) * lh + g.labelSize * CAP_CENTER;

                return (
                  <g key={`${l.kind}-${l.depth}`}>
                    <line
                      x1={onLeft ? geom.axisX : geom.wellRight}
                      y1={l.y}
                      x2={onLeft ? geom.wellLeft : geom.rightAxisX}
                      y2={l.y}
                      stroke={l.color}
                      strokeWidth={g.lineWidth * (isHovered ? 2.8 : 1.6)}
                    />
                    {g.showLabels && isFluid && (
                      // Жидкость: глубина + цветной маркер. Название доступно во всплывающей подсказке.
                      // Наведение на подпись подсвечивает секцию так же, как наведение на саму жидкость.
                      <g
                        style={{ cursor: "pointer" }}
                        onPointerEnter={
                          l.fluidId !== undefined
                            ? (e) => handleFluidHover(e, l.fluidId!)
                            : undefined
                        }
                        onPointerMove={
                          l.fluidId !== undefined
                            ? (e) => handleFluidHover(e, l.fluidId!)
                            : undefined
                        }
                        onPointerLeave={
                          l.fluidId !== undefined ? handleFluidLeave : undefined
                        }
                      >
                        <title>{l.title}</title>
                        {/* Прозрачная зона захвата — курсор ловится и в промежутках между маркером и числом. */}
                        <rect
                          x={onLeft ? swatchX : anchorX}
                          y={l.y - depthFontSize * 0.85}
                          width={
                            onLeft
                              ? anchorX - swatchX
                              : swatchX + swatch - anchorX
                          }
                          height={depthFontSize * 1.7}
                          fill="transparent"
                        />
                        {/* Под курсором — цветная подложка под числом, чтобы заголовок выделялся. */}
                        {isHovered && (
                          <rect
                            x={(onLeft ? anchorX - depthW : anchorX) - 4}
                            y={l.y - depthFontSize * 0.72}
                            width={depthW + 8}
                            height={depthFontSize * 1.44}
                            rx={3}
                            fill={l.color}
                          />
                        )}
                        <rect
                          x={swatchX}
                          y={l.y - swatch / 2}
                          width={swatch}
                          height={swatch}
                          rx={swatch * 0.22}
                          fill={l.color}
                          stroke={palette.swatchBorder}
                          strokeWidth={isHovered ? 1 : 0.5}
                        />
                        <text
                          x={anchorX}
                          y={l.y + depthFontSize * CAP_CENTER}
                          textAnchor={anchor}
                          fontSize={depthFontSize}
                          fontWeight="bold"
                        >
                          {depthStr}
                        </text>
                      </g>
                    )}
                    {g.showLabels &&
                      !isFluid &&
                      (fits ? (
                        // Помещается в одну строку: глубина у ствола, название — снаружи.
                        <text
                          x={anchorX}
                          y={baselineAt(l.y)}
                          textAnchor={anchor}
                        >
                          {onLeft ? (
                            <>
                              <tspan opacity={0.75}>{outer} </tspan>
                              <tspan fontWeight="bold">{depthStr}</tspan>
                            </>
                          ) : (
                            <>
                              <tspan fontWeight="bold">{depthStr}</tspan>
                              <tspan opacity={0.75}> {outer}</tspan>
                            </>
                          )}
                        </text>
                      ) : (
                        // Не помещается: переносим по строкам, блок центрируем по коннектору.
                        <text x={anchorX} y={l.y} textAnchor={anchor}>
                          {lines.map((ln, i) => {
                            const isDepth = i === lines.length - 1;
                            return (
                              <tspan
                                key={i}
                                x={anchorX}
                                dy={i === 0 ? startDy : lh}
                                fontWeight={isDepth ? "bold" : undefined}
                                opacity={isDepth ? 1 : 0.75}
                              >
                                {ln}
                              </tspan>
                            );
                          })}
                        </text>
                      ))}
                  </g>
                );
              })}
            </g>
          )}
        </svg>

        {hover && hoveredFluid && (
          <FluidTooltip
            fluid={hoveredFluid}
            at={hover}
            box={hostSize}
            unit={unitLabel}
            precision={g.precision}
            palette={palette}
          />
        )}
      </div>

      {showLegend && (
        <Legend
          fluids={fluids}
          fontSize={13}
          color={g.labelColor}
          swatchBorder={palette.swatchBorder}
        />
      )}
    </div>
  );
}

/**
 * Компонент отображения процесса цементирования скважины.
 * Обёрнут в memo — родитель может обновлять `fluids` каждую секунду без лишних перерисовок шкалы.
 */
export const WellCementingChart = memo(WellCementingBase);
export default WellCementingChart;
